import { useRef } from "react"
import { Swiper, SwiperSlide } from "swiper/react"
import { Navigation, Pagination } from "swiper/modules"
import { motion, useInView } from "framer-motion"
import { Link } from "react-router-dom"
import { serviciosTopografia } from "../../data/serviciosTopografia"

import "swiper/css"
import "swiper/css/navigation"
import "swiper/css/pagination"


export default function ServiciosTopografiaCarousel() {
    const containerRef = useRef(null);
    const isInView = useInView(containerRef, { once: true, margin: "-100px" });

    return (
        <section
            ref={containerRef}
            id="topografia"
            className="relative bg-[#FAFAFA] bg-gray-50 py-24 sm:py-32 overflow-x-clip"
        >
            {/* Decoración sutil de fondo */}
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                {/* HEADER PROFESIONAL */}
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        animate={isInView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.7 }}
                        className="max-w-2xl"
                    >
                        <span className="text-[#F26B1D] font-bold tracking-[0.2em] text-xs uppercase mb-4 block">
                            Ingeniería de Precisión
                        </span>
                        <h2 className="text-4xl sm:text-5xl font-extrabold text-gray-900 tracking-tight leading-none">
                            Servicios <span className="text-[#F26B1D]">Topográficos</span>
                        </h2>
                        <p className="text-gray-500 mt-6 text-lg leading-relaxed">
                            Soluciones de topografía y catastro con precisión garantizada y tecnología de vanguardia.
                        </p>
                    </motion.div>

                    {/* Controles de Navegación Estilizados */}
                    <div className="flex gap-3 mb-2">
                        <button className="swiper-prev group w-12 h-12 rounded-full border border-gray-200 flex items-center justify-center hover:border-[#F26B1D] transition-all duration-300 shadow-sm hover:shadow-md">
                            <span className="text-gray-400 group-hover:text-[#F26B1D] transition-colors">←</span>
                        </button>
                        <button className="swiper-next group w-12 h-12 rounded-full bg-gray-900 text-white flex items-center justify-center hover:bg-[#F26B1D] transition-all duration-300 shadow-xl">
                            <span>→</span>
                        </button>
                    </div>
                </div>

                {/* CARRUSEL DE SERVICIOS */}
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="w-[98%] lg:w-[100%] mx-auto"
                >
                    <Swiper
                        modules={[Navigation, Pagination]}
                        className="pb-24 pt-16 px-2 [&_.swiper-pagination]:!bottom-0"
                        navigation={{
                            nextEl: ".swiper-next",
                            prevEl: ".swiper-prev"
                        }}
                        pagination={{
                            clickable: true,
                            dynamicBullets: true
                        }}
                        spaceBetween={28}
                        breakpoints={{
                            0: { slidesPerView: 1.2, spaceBetween: 20 },
                            640: { slidesPerView: 2.2 },
                            1024: { slidesPerView: 3 },
                            1280: { slidesPerView: 4 }
                        }}
                    >
                        {serviciosTopografia.map((servicio, index) => (
                            <SwiperSlide key={index} className="h-auto py-8">
                                <motion.div
                                    whileHover={{ y: -12 }}
                                    transition={{ duration: 0.4, ease: "easeOut" }}
                                    className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-500 h-full flex flex-col overflow-hidden"
                                >
                                    {/* ✅ Altura fija en imagen */}
                                    <div className="relative h-48 overflow-hidden flex-shrink-0">
                                        <img
                                            src={servicio.img}
                                            alt={servicio.titulo}
                                            loading="lazy"
                                            className="w-full h-full object-cover transition duration-700 group-hover:scale-110"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                    </div>

                                    {/* Contenido */}
                                    <div className="p-7 flex flex-col flex-grow">

                                        {/* ✅ Título: 2 líneas fijas */}
                                        <h3 className="text-xl font-bold text-gray-900 group-hover:text-[#F26B1D] transition-colors duration-300 min-h-[56px] line-clamp-2">
                                            {servicio.titulo}
                                        </h3>

                                        {/* ✅ Descripción: 3 líneas fijas, ocupa el espacio disponible */}
                                        <p className="text-gray-500 mt-3 text-sm leading-relaxed line-clamp-3 min-h-[60px]">
                                            {servicio.desc}
                                        </p>

                                        {/* ✅ Botón siempre al fondo */}
                                        <div className="mt-auto pt-5 border-t border-gray-100">
                                            <Link
                                                to={`/servicios/topografia/${servicio.slug}`}
                                                className="inline-flex items-center text-sm font-bold text-[#F26B1D] group/link"
                                            >
                                                Saber más
                                                <span className="ml-2 transform group-hover/link:translate-x-1 transition-transform">→</span>
                                            </Link>
                                        </div>
                                    </div>
                                </motion.div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </motion.div>

                {/* BOTÓN DE ACCIÓN FINAL (Opcional, pero recomendado) */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }} // Añadimos un pequeño movimiento hacia arriba
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 1, duration: 0.8 }}
                    className="flex justify-center mt-10 px-4" // px-4 evita que el botón toque los bordes del celular
                >
                    <Link
                        to="/servicios/topografia"
                        className="bg-gray-900 text-white 
            px-6 py-4 md:px-10 
            rounded-full font-bold 
            hover:bg-[#F26B1D] transition-all duration-300 shadow-lg hover:shadow-2xl 
            transform hover:-translate-y-1
            text-sm md:text-base text-center
            w-full md:w-auto max-w-xs md:max-w-none
        "
                    >
                        {/* Cambiamos el texto según el tamaño de pantalla */}
                        <span className="block md:hidden">Ver servicios</span>
                        <span className="hidden md:block">Ver todos los servicios</span>
                    </Link>
                </motion.div>
            </div>
        </section>
    );
}